import api from '@/services/api';
import type { CampaignConfigSetting } from '../types/campaign.types';
import type { ConfigOptionRecord } from './smr-admin';
import type { CthfRulesFormValues } from '../schemas/cthfRules.schema';

const smrPath = '/smr';

/** The master gate. Rendered separately and EXCLUDED from the capability checkbox list. */
export const CTHF_ENABLED_KEY = 'cthf_enabled';

/**
 * Display order for the capability checkboxes, taken from the legacy "CTHF Details"
 * screen (Trello kLBzR9lC, 'CTHF settings.png'): the first five fill the left column,
 * the remainder the right. The catalogue has NO order column and the list endpoint sorts
 * by key_name (alphabetical), which would scramble the admin's familiar reading order —
 * hence an explicit constant. It is an ORDERING HINT ONLY, never a whitelist: any
 * catalogue key not listed here still renders, appended after these in catalogue
 * (alphabetical) order, so a Finance-added capability appears with no frontend edit.
 */
export const CTHF_CAPABILITY_ORDER = [
    'collect_deposits',
    'collect_goodwill',
    'pay_admin_fee',
    'pay_direct_debit_fee',
    'include_write_off_in_batch',
    'pay_sales_commission',
    'pay_cancellation_fee',
    'allow_cancellation',
    'allow_ownership_transfer',
] as const;

/** money_handling catalogue rows minus the master gate, in display order. */
export const orderCapabilityOptions = (options: ConfigOptionRecord[]): ConfigOptionRecord[] => {
    const rank = (key: string) => {
        const i = (CTHF_CAPABILITY_ORDER as readonly string[]).indexOf(key);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return options
        .filter((o) => o.key_name !== CTHF_ENABLED_KEY)
        .sort((a, b) => rank(a.key_name) - rank(b.key_name) || a.key_name.localeCompare(b.key_name));
};

/**
 * Defensive boolean coercion. `value` is JSON-cast on the backend and Laravel's `boolean`
 * rule accepts 1 / 0 / "1" / "0" / true / false, so a row authored earlier through the
 * GENERIC panel or the Apply-Catalog modal may not be a JSON true/false. A bare
 * Boolean("false") would read as true and silently tick a capability the admin never set.
 */
const toBool = (value: unknown): boolean =>
    value === true || value === 1 || value === '1' || value === 'true';

/**
 * Seed the form from the loaded settings, keyed on the LIVE capability list so the record's
 * key set always matches what is rendered. Missing rows -> false. A stored row whose key is
 * no longer in the catalogue is ignored (not rendered, not rewritten, not deleted).
 */
export const settingsToCthfForm = (
    settings: CampaignConfigSetting[],
    capabilityKeys: readonly string[],
): CthfRulesFormValues => {
    const byKey = new Map(settings.map((s) => [s.key, s.value]));
    return {
        cthf_enabled: toBool(byKey.get(CTHF_ENABLED_KEY)),
        capabilities: Object.fromEntries(capabilityKeys.map((k) => [k, toBool(byKey.get(k))])),
    };
};

export interface CthfSettingRow {
    key: string;
    value: boolean;
    config_option_id?: string;
}

/**
 * Build the attach payload. Always write cthf_enabled; write the capability rows ONLY when
 * enabled (Decision D2 — disabling leaves the prior authored capability rows untouched so
 * re-enabling restores them). config_option_id sourced from the catalogue map (D5).
 */
export const cthfFormToSettings = (
    values: CthfRulesFormValues,
    optionIdByKey: Record<string, string | undefined>,
    capabilityKeys: readonly string[],
): CthfSettingRow[] => {
    const rows: CthfSettingRow[] = [
        { key: CTHF_ENABLED_KEY, value: values.cthf_enabled, config_option_id: optionIdByKey[CTHF_ENABLED_KEY] },
    ];
    if (values.cthf_enabled) {
        for (const key of capabilityKeys) {
            rows.push({ key, value: Boolean(values.capabilities[key]), config_option_id: optionIdByKey[key] });
        }
    }
    return rows;
};

/**
 * Single POST attach call — idempotent upsert-by-key (CampaignConfigSettingService::attach).
 * Mirrors fetchCampaignConfigSettings's envelope-unwrap.
 */
export const saveCthfConfigSettings = async (
    campaignId: string,
    rows: CthfSettingRow[],
): Promise<CampaignConfigSetting[]> => {
    const response = await api.post(`${smrPath}/campaigns/${campaignId}/config-settings`, { settings: rows });
    return (response.data.data ?? response.data) as CampaignConfigSetting[];
};
